import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTeam } from '@/hooks/useTeam'
import { useEdition } from '@/hooks/useEdition'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import StudyHeader from '@/components/researcher/monitoring/StudyHeader'
import KpiStrip, { type KpiItem } from '@/components/researcher/monitoring/KpiStrip'
import ResultsReadyBanner from '@/components/researcher/monitoring/ResultsReadyBanner'
import RespondentsTable, {
  type RespondentRow,
  type RespondentStatus,
} from '@/components/researcher/monitoring/RespondentsTable'
import LiveActivity from '@/components/researcher/monitoring/LiveActivity'
import Funnel from '@/components/researcher/monitoring/Funnel'
import type { FunnelStep, LiveEvent } from '@/components/researcher/monitoring/monitoringMockData'
import MonitoringEmpty from '@/components/researcher/monitoring/MonitoringEmpty'
import StudyMonitoringSkeleton from '@/components/researcher/monitoring/StudyMonitoringSkeleton'
import ExportStudyDialog from '@/components/researcher/monitoring/ExportStudyDialog'
import InviteRespondentsDialog from '@/components/researcher/monitoring/InviteRespondentsDialog'
import EmotionAnalysisPanel from '@/components/researcher/emotions/EmotionAnalysisPanel'
import { createRelativeFormatter } from '@/components/researcher/studyRows'
import { projectService } from '@/services/projectService'
import type { StudyDashboard, StudyDashboardRespondent } from '@/services/projectService'
import { participantService } from '@/services/participantService'
import type { Project, ProjectParticipant } from '@/services/projectTypes'

/** Minimum completed responses before the "results ready" banner shows. */
const RESULTS_READY_THRESHOLD = 30

/** Maps a participant lifecycle status to the table's respondent status. */
function toRespondentStatus(s: StudyDashboardRespondent['status']): RespondentStatus {
  switch (s) {
    case 'completed':
      return 'done'
    case 'assigned':
    case 'in_progress':
      return 'progress'
    case 'cancelled':
    case 'abandoned':
      return 'drop'
    default:
      return 'invited'
  }
}

/** m:ss from a duration in seconds. */
function fmtDuration(seconds: number | null): string {
  if (seconds == null || seconds <= 0) return '—'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/** HH:MM clock from an ISO timestamp, in the user's locale. */
function fmtClock(iso: string | null, locale: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
  } catch {
    return '—'
  }
}

/** Relative-time amount/unit for the live feed. */
function relTime(iso: string): { amount: number; unit: 'sec' | 'min' } {
  const diffMs = Date.now() - new Date(iso).getTime()
  const sec = Math.max(0, Math.round(diffMs / 1000))
  if (sec < 90) return { amount: sec, unit: 'sec' }
  return { amount: Math.round(sec / 60), unit: 'min' }
}

/**
 * Study screen mounted at /studies/:id (project id).
 *
 * Pro gets two tabs: live Monitoring (KPIs, funnel, activity, respondents — all
 * from the real /study-dashboard/ endpoint) and Emotion analysis (the player +
 * timeline panel). No fabricated numbers: fields the backend can't supply show
 * an honest empty state.
 *
 * FaceWise has no research workshop to monitor — no funnel, no live feed, no
 * respondent table, no export, no email invitations — so its study screen *is*
 * the results panel, with no tab row above it. The tab-row element itself stays
 * mounted either way: the panel portals its filter control into it, and without
 * that slot the filter falls back to a row of its own.
 */
export default function StudyMonitoring() {
  const { t, i18n } = useTranslation('monitoring')
  const navigate = useNavigate()
  const { id } = useParams()
  const projectId = Number(id)
  const [searchParams, setSearchParams] = useSearchParams()
  const { isBasic } = useEdition()
  // Both editions have both views. Monitoring — the funnel, the respondent
  // table, the live activity — moved into FaceWise on 7 September 2026, when
  // the team settled that the entry plan is limited by volume rather than by
  // features: somebody paying a subscription gets to see who answered.
  const tab = searchParams.get('tab') === 'emotions' ? 'emotions' : 'monitoring'
  const respondentParam = searchParams.get('respondent')
  const selectedRespondentId =
    respondentParam != null && respondentParam !== '' && Number.isFinite(Number(respondentParam))
      ? Number(respondentParam)
      : null

  const [project, setProject] = useState<Project | null>(null)
  const { inTeam, isOwner } = useTeam()

  /** Share the study with the team, or take it back. Optimistic: the switch
   *  flips immediately and reverts if the backend refuses. */
  const handleVisibilityChange = useCallback(
    async (next: 'private' | 'team') => {
      if (!project) return
      const previous = project.visibility ?? 'private'
      setProject({ ...project, visibility: next })
      try {
        await projectService.updateProject(project.id, { visibility: next })
      } catch {
        setProject((current) => (current ? { ...current, visibility: previous } : current))
      }
    },
    [project],
  )
  const [participants, setParticipants] = useState<ProjectParticipant[] | null>(null)
  const [dashboard, setDashboard] = useState<StudyDashboard | null>(null)
  const [error, setError] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  /** Respondent row pending deletion, and the confirm modal's own state — a
   *  bespoke dialog rather than window.confirm, matching the study-delete flow. */
  const [respondentToDelete, setRespondentToDelete] = useState<RespondentRow | null>(null)
  const [deletingRespondent, setDeletingRespondent] = useState(false)
  const [deleteRespondentError, setDeleteRespondentError] = useState<string | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteSummary, setInviteSummary] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  /** Bumped on manual refresh — remounts the emotion panel so it refetches. */
  const [refreshKey, setRefreshKey] = useState(0)
  /** Tab-row slot the emotion panel portals its filter control into (see below). */
  const [filterSlot, setFilterSlot] = useState<HTMLElement | null>(null)

  /**
   * Everything the screen needs, settled independently so one failure can't
   * blank out the others. The participants list feeds the export dialog and the
   * dashboard feeds the monitoring tab; both editions have both, so both are
   * always asked for.
   */
  const fetchAll = useCallback(
    () =>
      Promise.allSettled([
        projectService.getProject(projectId),
        projectService.getProjectParticipants(projectId),
        projectService.getStudyDashboard(projectId),
      ]),
    [projectId],
  )

  useEffect(() => {
    let cancelled = false
    setProject(null)
    setParticipants(null)
    setDashboard(null)
    setError(false)

    if (!Number.isFinite(projectId)) {
      setError(true)
      setParticipants([])
      return
    }

    void fetchAll().then(([projRes, partsRes, dashRes]) => {
      if (cancelled) return
      if (projRes.status === 'fulfilled') setProject(projRes.value)
      else setError(true)
      setParticipants(partsRes.status === 'fulfilled' ? partsRes.value : [])
      setDashboard(dashRes.status === 'fulfilled' ? dashRes.value : null)
    })

    return () => {
      cancelled = true
    }
  }, [projectId, fetchAll])

  const loading = participants === null

  const counts3 = dashboard?.counts
  const invited = counts3?.invited ?? 0
  const started = counts3?.started ?? 0
  const completed = counts3?.completed ?? 0
  const pct = (n: number) => (invited > 0 ? Math.round((n / invited) * 100) : 0)

  const rows = useMemo<RespondentRow[]>(() => {
    const rel = createRelativeFormatter(i18n.language)
    return (dashboard?.respondents ?? []).map((r) => {
      // Invited respondents keep their real email; anonymous walk-ins are
      // identified by their face-preview avatar + when they took part
      // ("16 minut temu") rather than an opaque R-#id.
      const when = r.completed_at ?? r.started_at
      const id =
        !r.is_anonymous && r.display_name
          ? r.display_name
          : when
            ? rel(when)
            : `R-#${r.id}`
      return {
        id,
        respondentId: r.id,
        thumbnailUrl: r.thumbnail_url,
        status: toRespondentStatus(r.status),
        started: fmtClock(r.started_at, i18n.language),
        duration: fmtDuration(r.duration_seconds),
        device: r.device || 'none',
        age: r.demographics['Wiek'] ?? r.demographics['Age'] ?? '',
        city: r.demographics['Miasto'] ?? r.demographics['City'] ?? '',
        source: r.is_anonymous ? 'anonymous' : 'invited',
      }
    })
  }, [dashboard, i18n.language])

  const demographicsById = useMemo(
    () => Object.fromEntries((dashboard?.respondents ?? []).map((r) => [r.id, r.demographics])),
    [dashboard],
  )

  const counts = useMemo(() => {
    const by = (s: RespondentStatus) => rows.filter((r) => r.status === s).length
    return { all: rows.length, done: by('done'), progress: by('progress'), drop: by('drop'), invited: by('invited') }
  }, [rows])

  const kpis = useMemo<KpiItem[]>(() => {
    const avg = dashboard?.avg_duration_seconds ?? null
    const remaining = dashboard?.remaining_to_target
    return [
      { label: t('kpi.invited'), value: String(invited), suffix: t('kpi.people') },
      { label: t('kpi.started'), value: String(started), suffix: `${pct(started)}%` },
      { label: t('kpi.completed'), value: String(completed), suffix: `${pct(completed)}%` },
      { label: t('kpi.avgTime'), value: fmtDuration(avg), suffix: t('kpi.minSec') },
      { label: t('kpi.today'), value: String(dashboard?.today_completed ?? 0), suffix: t('kpi.people') },
      {
        label: t('kpi.remaining'),
        value: remaining == null ? '—' : String(remaining),
        suffix: t('kpi.people'),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, dashboard, invited, started, completed])

  // Funnel: real counts; backend keys → existing i18n step keys, % of invited.
  const funnelSteps = useMemo<FunnelStep[]>(() => {
    const keyMap: Record<string, FunnelStep['key']> = {
      invited: 'invited',
      started: 'started',
      viewed: 'viewedStimuli',
      completed: 'completedQuestions',
    }
    const base = invited || 1
    return (dashboard?.funnel ?? [])
      .filter((s) => keyMap[s.key])
      .map((s) => ({ key: keyMap[s.key], n: s.n, pct: Math.round((s.n / base) * 100) }))
  }, [dashboard, invited])

  const liveEvents = useMemo<LiveEvent[]>(() => {
    const kindMap: Record<string, LiveEvent['kind']> = { completed: 'completed', started: 'startedStimulus' }
    return (dashboard?.activity ?? [])
      .filter((e) => kindMap[e.kind])
      .map((e) => {
        const { amount, unit } = relTime(e.at)
        return { who: e.who.replace('Respondent #', 'R-#'), kind: kindMap[e.kind], amount, unit }
      })
  }, [dashboard])

  const heatmap = dashboard?.heatmap ?? []

  const projectName = project?.name ?? ''
  const headerStatus = project?.is_active === false ? 'done' : 'active'
  const createdLabel = project?.created_at
    ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' }).format(new Date(project.created_at))
    : undefined
  const link = project?.share_token ? `${window.location.origin}/#/r/${project.share_token}` : ''
  const isEmpty = !loading && invited === 0

  const handleCopyLink = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable */
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await projectService.deleteProject(projectId)
      navigate('/projects')
    } catch {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  /** Remove one respondent — recording, analysis, questionnaire answer, thumbnail
   *  and all (Z-48) — then refetch so the list and the aggregated KPIs/funnel/
   *  emotion results recalculate without the deleted respondent's data. */
  const handleDeleteRespondent = async () => {
    if (!respondentToDelete) return
    setDeletingRespondent(true)
    setDeleteRespondentError(null)
    try {
      await participantService.deleteProjectParticipant(respondentToDelete.respondentId)
      setRespondentToDelete(null)
      await refresh()
    } catch {
      setDeleteRespondentError(t('respondents.deleteFailed'))
    } finally {
      setDeletingRespondent(false)
    }
  }

  /** Refetch everything in place (no blank-out): project, participants,
   *  dashboard — and remount the emotion panel so its data refetches too. */
  const refresh = async () => {
    if (!Number.isFinite(projectId) || refreshing) return
    setRefreshing(true)
    const [projRes, partsRes, dashRes] = await fetchAll()
    if (projRes.status === 'fulfilled') setProject(projRes.value)
    if (partsRes.status === 'fulfilled') setParticipants(partsRes.value)
    if (dashRes.status === 'fulfilled') setDashboard(dashRes.value)
    setRefreshKey((k) => k + 1)
    setRefreshing(false)
  }

  const handleInviteSent = async (summary: string) => {
    setInviteOpen(false)
    setInviteSummary(summary)
    // Refresh so freshly-invited respondents appear in the table/KPIs.
    await refresh()
  }

  const setTab = (next: 'monitoring' | 'emotions') => {
    const params = new URLSearchParams(searchParams)
    if (next === 'monitoring') {
      params.delete('tab')
      params.delete('respondent')
    } else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }

  // Open the emotion analysis drilled into a single respondent's curves.
  const handleSelectRespondent = (respondentId: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('tab', 'emotions')
    params.set('respondent', String(respondentId))
    setSearchParams(params)
  }

  return (
    <ResearcherShell active="studies" crumbs={[t('crumb'), projectName || t('crumb')]} onPrimaryAction={() => {}}>
      {loading ? (
        <StudyMonitoringSkeleton variant={tab === 'emotions' ? 'results' : 'monitoring'} />
      ) : (
        <Stack spacing={2.25} sx={{ maxWidth: 1340, mx: 'auto' }}>
          <StudyHeader
            title={projectName || t('crumb')}
            status={headerStatus}
            link={link}
            client={project ? (project.client?.trim() ?? '') : undefined}
            createdLabel={createdLabel}
            owner={project?.created_by_name || undefined}
            onDelete={() => setConfirmDelete(true)}
            // The export is open in both editions — these are the researcher's
            // own results, and a plan tier is not a reason to withhold them.
            // The access code and team visibility are still Pro, and a basic
            // study has neither (the backend forces both off), so the header is
            // given nothing to draw for them.
            onExport={() => setExportOpen(true)}
            onCopyLink={handleCopyLink}
            copied={copied}
            accessCode={!isBasic && project?.require_access_code ? project.access_code : undefined}
            visibility={!isBasic && inTeam ? (project?.visibility ?? 'private') : undefined}
            canChangeVisibility={project?.is_own !== false || isOwner}
            onVisibilityChange={handleVisibilityChange}
          />

          {/* The tab row doubles as the filter's home: EmotionAnalysisPanel
              portals its control into the slot below, and without that slot the
              filter falls back to an inline row of its own. */}
          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ flex: 1, minHeight: 0, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' } }}
            >
              <Tab value="monitoring" label={t('tabs.monitoring')} />
              <Tab value="emotions" label={t('tabs.emotions')} />
            </Tabs>
            <Box ref={setFilterSlot} sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }} />
            <Tooltip title={t('refresh')}>
              <span>
                <Button
                  aria-label={t('refresh')}
                  onClick={() => void refresh()}
                  disabled={refreshing}
                  variant="outlined"
                  color="inherit"
                  disableElevation
                  startIcon={
                    <RefreshIcon
                      sx={{
                        fontSize: 19,
                        animation: refreshing ? 'fwSpin 0.8s linear infinite' : 'none',
                        '@keyframes fwSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                      }}
                    />
                  }
                  sx={{
                    mr: 0.5,
                    borderRadius: 5,
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: 'background.paper',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {t('refreshShort')}
                </Button>
              </span>
            </Tooltip>
          </Box>

          {tab === 'emotions' ? (
            <EmotionAnalysisPanel
              key={`${selectedRespondentId ?? 'aggregate'}-${refreshKey}`}
              projectId={projectId}
              initialRespondentId={selectedRespondentId}
              filterSlot={filterSlot}
            />
          ) : isEmpty || error ? (
            <MonitoringEmpty
              onCopyLink={handleCopyLink}
              // Email invitations come out of the address book, which is Pro.
              // FaceWise recruits with the link and the QR code, so the second
              // button is absent rather than present and refused.
              onSendInvites={isBasic ? undefined : () => setInviteOpen(true)}
              copied={copied}
            />
          ) : (
            <>
              <KpiStrip items={kpis} />
              {completed >= RESULTS_READY_THRESHOLD && (
                <ResultsReadyBanner completed={completed} onViewResults={() => setTab('emotions')} />
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.55fr 1fr' }, gap: 2.25 }}>
                <RespondentsTable
                  rows={rows}
                  invitedCount={invited}
                  counts={counts}
                  onInvite={isBasic ? undefined : () => setInviteOpen(true)}
                  onSelectRespondent={handleSelectRespondent}
                  onDeleteRespondent={setRespondentToDelete}
                />
                <Stack spacing={2.25}>
                  <LiveActivity events={liveEvents} heatmap={heatmap} />
                  <Funnel steps={funnelSteps} />
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      )}

      <ExportStudyDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        projectId={projectId}
        studyName={projectName || t('crumb')}
        participants={participants ?? []}
        demographics={demographicsById}
      />

      {/* Email invitations belong to Pro: they are sent from the address book,
          which a FaceWise account does not have. Nothing here can open the
          dialog, and mounting it would wire it to an endpoint that refuses. */}
      {!isBasic && (
        <>
          <InviteRespondentsDialog
            open={inviteOpen}
            projectId={projectId}
            onClose={() => setInviteOpen(false)}
            onSent={handleInviteSent}
          />

          <Snackbar
            open={!!inviteSummary}
            autoHideDuration={6000}
            onClose={() => setInviteSummary(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="success" onClose={() => setInviteSummary(null)} sx={{ width: '100%' }}>
              {inviteSummary}
            </Alert>
          </Snackbar>
        </>
      )}

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('header.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('header.deleteConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting} color="inherit">
            {t('header.cancel')}
          </Button>
          <Button onClick={handleDelete} disabled={deleting} variant="contained" color="error">
            {deleting ? <CircularProgress size={18} color="inherit" /> : t('header.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!respondentToDelete}
        onClose={() => !deletingRespondent && setRespondentToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t('respondents.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('respondents.deleteConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRespondentToDelete(null)} disabled={deletingRespondent} color="inherit">
            {t('header.cancel')}
          </Button>
          <Button onClick={handleDeleteRespondent} disabled={deletingRespondent} variant="contained" color="error">
            {deletingRespondent ? <CircularProgress size={18} color="inherit" /> : t('respondents.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!deleteRespondentError}
        autoHideDuration={6000}
        onClose={() => setDeleteRespondentError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setDeleteRespondentError(null)} sx={{ width: '100%' }}>
          {deleteRespondentError}
        </Alert>
      </Snackbar>
    </ResearcherShell>
  )
}
